import { normalizeReportData } from './reportDataMerge.js';

export const srmPlanDocumentPrompt = (name: string, description: string) => `Draft the facility security plan "${name}" using this brief: ${description}
Return a client-readable Markdown document, starting with its title. Use clear section headings, short paragraphs, numbered procedures, checklists, and tables for roles, responsibilities, triggers, and recovery targets when useful.
Return the document itself without wrapping it in a code block, JSON, HTML, application code, or commentary about writing the document.
Use supplied context to write the plan; do not copy input payloads or generator instructions into it.
Keep proposed procedures distinct from verified site facts. Identify missing client inputs and approvals explicitly; never invent contacts, site inspections, resources, or approved procedures.`;

const metadata = new Set(['reportType', 'generatedAt', 'createdAt', 'updatedAt', 'model', 'modelId', 'workflow',
    'groundingMetadata', 'usageMetadata', 'thought', 'thoughtSignature', 'dataGaps', 'evidenceGaps']);
const wrappers = new Set(['content', 'markdown', 'document', 'text', 'body', 'plan', 'report']);
const readable = (key: string) => key.replace(/([a-z\d])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').replace(/^./, letter => letter.toUpperCase());
const code = /^(?:\s*(?:import\s+.+\s+from\s+['"]|export\s+(?:default|const|function|class)\b|(?:const|let|var)\s+\w+\s*=|(?:async\s+)?function\s+\w+\s*\(|(?:body|:root|\.[\w-]+|#[\w-]+)\s*\{))/m;

export function readSrmPlanDocument(value: unknown): { markdown: string; gaps: string[] } {
    const gaps = new Set<string>();
    const unreadable = () => gaps.add('Some plan content could not be read. Regenerate this plan to restore missing details.');
    const heading = (label: string, level: number) => `${'#'.repeat(Math.min(level, 6))} ${label}`;
    const convert = (input: any, depth = 0, level = 1): string => {
        if (input == null)
            return '';
        if (depth > 10) {
            unreadable();
            return '';
        }
        if (typeof input === 'number' || typeof input === 'boolean')
            return String(input);
        if (typeof input === 'string') {
            let text = input.replace(/\r\n?/g, '\n').trim();
            if (!text)
                return '';
            if (/^Error:\s/i.test(text)) {
                unreadable();
                return '';
            }
            if (/^(?:\{\s*["}]|\[\s*(?:\{|"|\]))/.test(text) || /^"[\s\S]*"$/.test(text)) {
                try {
                    return convert(JSON.parse(text), depth + 1, level);
                } catch {
                    unreadable();
                    return '';
                }
            }
            text = text.replace(/(^|\n)[ \t]*(`{3,}|~{3,})[ \t]*([^\n]*)\n([\s\S]*?)(?:\n[ \t]*\2[ \t]*(?=\n|$)|$)/g,
                (_match, before: string, _fence: string, language: string, body: string) => {
                    const format = language.trim().toLowerCase();
                    if (['', 'markdown', 'md', 'text', 'txt', 'plaintext', 'json', 'html'].includes(format))
                        return `${before}${convert(body, depth + 1, level)}`;
                    unreadable();
                    return before;
                });
            // Suppress implementation content while retaining ordinary report HTML for safe rendering.
            text = text.replace(/<(script|style)\b[^>]*>[\s\S]*?(?:<\/\1\s*>|$)/gi, () => {
                unreadable();
                return '';
            }).replace(/<!doctype[^>]*>|<\/?(?:html|head|body)\b[^>]*>/gi, '');
            if (code.test(text)) {
                unreadable();
                return '';
            }
            return text.replace(/`([^`\n]+)`/g, '$1').trim();
        }
        if (Array.isArray(input)) {
            const rows = input.filter(item => item != null);
            const keys = rows[0] && typeof rows[0] === 'object' && !Array.isArray(rows[0]) ? Object.keys(rows[0]).filter(key => !metadata.has(key)) : [];
            const sections = keys.some(key => ['heading', 'title'].includes(key)) && keys.some(key => ['content', 'body', 'description', 'text'].includes(key));
            if (!sections && keys.length > 1 && keys.length <= 8 && rows.every(row => typeof row === 'object' && !Array.isArray(row) &&
                Object.keys(row).filter(key => !metadata.has(key)).every(key => keys.includes(key)) && keys.every(key => row[key] == null || ['string', 'number', 'boolean'].includes(typeof row[key])))) {
                const cell = (item: unknown) => convert(item, depth + 1).replace(/\|/g, '\\|').replace(/\n+/g, '; ');
                return `| ${keys.map(readable).join(' | ')} |\n| ${keys.map(() => '---').join(' | ')} |\n` +
                    rows.map(row => `| ${keys.map(key => cell(row[key])).join(' | ')} |`).join('\n');
            }
            return rows.map(item => typeof item === 'object' ? convert(item, depth + 1, level) : `- ${convert(item, depth + 1, level)}`).filter(Boolean).join('\n\n');
        }
        if (typeof input !== 'object')
            return '';
        const normalized = normalizeReportData(input);
        const keys = Object.keys(normalized).filter(key => !metadata.has(key) && !(key === 'type' && normalized.type === 'SrmDocument') &&
            !['result', 'response', 'candidates', 'fullReportData', 'resultData', 'reportData'].includes(key));
        if (keys.length === 1 && wrappers.has(keys[0]))
            return convert(normalized[keys[0]], depth + 1, level);
        const title = normalized.heading || normalized.title || normalized.name;
        const parts = typeof title === 'string' && title.trim() ? [heading(convert(title, depth + 1), level)] : [];
        for (const key of keys) {
            if (['heading', 'title', 'name'].includes(key))
                continue;
            const childLevel = Math.max(2, title ? level + 1 : level);
            const content = convert(normalized[key], depth + 1, childLevel);
            if (content)
                parts.push(wrappers.has(key) || key === 'sections' ? content : `${heading(readable(key), childLevel)}\n\n${content}`);
        }
        return parts.join('\n\n');
    };
    const markdown = convert(value).trim();
    if (!markdown)
        gaps.add('Plan content unavailable. Regenerate this plan.');
    return { markdown, gaps: [...gaps] };
}

export function generatedSrmPlanDocument(value: unknown): string {
    const document = readSrmPlanDocument(value);
    if (!document.markdown)
        throw new Error('Plan content unavailable. Regenerate this plan.');
    return document.markdown + (document.gaps.length ? `\n\n## Content needing review\n\n${document.gaps.join('\n\n')}` : '');
}
